<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  DxDataGrid,
  DxColumn,
  DxSorting,
  DxScrolling,
  DxButton as DxGridButton,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import {
  BottoPagePanel,
  ConfirmDialog,
  LookupBox,
  showError,
  showSuccess,
  bottoGridPaging,
} from '@botto/core';
import { getDirectionsLookup } from '../../lookups';
import { createGroupsAdapter, createGroupsStore } from '../../api';
import type { StreamGroup } from '../../api';
import { getSmppClientsLookup } from '../../api/adapters/accounts';

const { paging, pager } = bottoGridPaging();

const route = useRoute();
const router = useRouter();
const directionsLookup = getDirectionsLookup();
const smppClientsLookup = getSmppClientsLookup();
const grid = ref<InstanceType<typeof DxDataGrid> | null>(null);

const filterStreamId = ref<number | null>(
  route.query.streamId != null && Number.isFinite(Number(route.query.streamId))
    ? Number(route.query.streamId)
    : null,
);
const filterDirectionId = ref<number | null>(null);

const confirmOpen = ref(false);
const confirmTarget = ref<StreamGroup | null>(null);

const groupsStore = computed(() => {
  if (filterStreamId.value == null) return null;
  const store = createGroupsStore(filterStreamId.value);
  const directionId = filterDirectionId.value;
  if (directionId == null) return store;
  return {
    ...store,
    load(opts: any) {
      return store.load({
        ...opts,
        filter: { ...(opts?.filter ?? {}), direction_id: directionId },
      });
    },
  } as typeof store;
});

watch(filterStreamId, (id) => {
  const query = { ...route.query } as Record<string, string>;
  if (id != null) query.streamId = String(id);
  else delete query.streamId;
  void router.replace({ path: '/sms/routing-choices', query });
  grid.value?.instance?.refresh();
});

onMounted(async () => {
  await Promise.all([
    directionsLookup.ensureLoaded(),
    smppClientsLookup.ensureLoaded(),
  ]);
  grid.value?.instance?.refresh();
});

function directionLabel(row: StreamGroup): string {
  return directionsLookup.labelOf(row.direction_id);
}

function openCreate(): void {
  if (filterStreamId.value == null) {
    showError('Сначала выберите канал (клиент)');
    return;
  }
  void router.push(`/sms/routing-choices/${filterStreamId.value}/new`);
}

function openEdit(row: StreamGroup): void {
  if (row.id == null || filterStreamId.value == null) return;
  void router.push(`/sms/routing-choices/${filterStreamId.value}/${row.id}`);
}

function openTrafficDistribution(row: StreamGroup): void {
  if (row.id == null || filterStreamId.value == null) return;
  void router.push(`/sms/routing-choices/${filterStreamId.value}/${row.id}/routing`);
}

function refresh(): void {
  grid.value?.instance?.refresh();
}

function applyFilter(): void {
  refresh();
}

function confirmDelete(row: StreamGroup): void {
  confirmTarget.value = row;
  confirmOpen.value = true;
}

async function doDelete(): Promise<void> {
  if (!confirmTarget.value?.id || filterStreamId.value == null) return;
  try {
    const adapter = createGroupsAdapter(filterStreamId.value);
    await adapter.remove?.(confirmTarget.value.id);
    showSuccess('Удалено');
    confirmOpen.value = false;
    confirmTarget.value = null;
    refresh();
  } catch (e) {
    showError(e);
  }
}
</script>

<template>
  <BottoPagePanel title="Маршрутизация" standalone>
    <template #actions>
      <LookupBox
        v-model="filterStreamId"
        :lookup="smppClientsLookup"
        placeholder="Канал (клиент)"
        :show-clear-button="true"
        style="width: 220px"
      />
      <LookupBox
        v-model="filterDirectionId"
        :lookup="directionsLookup"
        placeholder="Направление"
        :show-clear-button="true"
        style="width: 220px"
      />
      <DxButton text="Применить" styling-mode="outlined" @click="applyFilter" />
      <DxButton
        icon="plus"
        text="Добавить"
        type="default"
        styling-mode="contained"
        :disabled="filterStreamId == null"
        @click="openCreate"
      />
      <DxButton icon="refresh" text="Обновить" styling-mode="outlined" @click="refresh" />
    </template>

    <div v-if="filterStreamId == null" class="rc-hint">
      Выберите канал (клиент), чтобы увидеть группы маршрутизации (stream + direction).
    </div>

    <DxDataGrid
      v-else
      ref="grid"
      :data-source="groupsStore"
      key-expr="id"
      :remote-operations="{ paging: true, sorting: true, filtering: false }"
      :show-borders="false"
      :show-row-lines="false"
      :show-column-lines="false"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :column-auto-width="true"
      :allow-column-resizing="true"
      :paging="paging"
      :pager="pager"
      height="100%"
      width="100%"
      @row-dbl-click="(e: any) => openEdit(e.data)"
    >
      <DxColumn data-field="id" caption="ID" data-type="number" :width="80" />
      <DxColumn data-field="name" caption="Название" />
      <DxColumn
        data-field="direction_id"
        caption="Направление"
        :calculate-display-value="directionLabel"
      />
      <DxColumn type="buttons" :width="280" caption="">
        <DxGridButton
          hint="Редактировать"
          icon="edit"
          :on-click="(e: any) => openEdit(e.row.data)"
        />
        <DxGridButton
          text="Распределение трафика"
          hint="Агрегаторы / %"
          icon="hierarchy"
          :on-click="(e: any) => openTrafficDistribution(e.row.data)"
        />
        <DxGridButton
          hint="Удалить"
          icon="trash"
          :on-click="(e: any) => confirmDelete(e.row.data)"
        />
      </DxColumn>

      <DxSorting mode="single" />
      <DxScrolling mode="standard" />
    </DxDataGrid>

    <ConfirmDialog
      v-if="confirmOpen"
      v-model:visible="confirmOpen"
      title="Удалить группу?"
      message="Действие необратимо."
      ok-text="Удалить"
      ok-type="danger"
      @ok="doDelete"
    />
  </BottoPagePanel>
</template>

<style scoped>
.rc-hint {
  padding: 24px;
  color: var(--dx-color-text-secondary, #666);
}
</style>
